import { connect } from 'react-redux'
import { MenuComponent } from './MenuComponent'
import type { Dispatch } from 'redux'
import type { RootState } from 'shared/store/reducers'
import type { PurchasableItem } from 'shared/store/ecommerce/order'
import {
    addMultipleProductsToCart,
    addProductToCart,
    setCurrentItem,
    setCurrentSuggestions,
    updateProductInCart
} from 'shared/store/ecommerce/order'
import { getMenuById } from 'shared/store/ecommerce/catalog'
import { getProductPrice, sumProductFinalPrices } from 'shared/utils/ecommerce'
import { featureCodes, menuSize } from 'shared/utils'
import { fetchAvailableFeatures } from 'shared/store/features'
import type { ECommerceMetadata, EventMetadata } from 'shared/store/metadata'
import {
    eCommerceMetadata,
    eventMetadata,
    eventsDefinitions,
    pageMetadata
} from 'shared/store/metadata'
import type { MetaShortDTO } from 'shared/types'
import { push } from 'connected-react-router'
import _ from 'lodash'

const { XL: MENU_SIZE_XL } = menuSize

const mapStateToProps = (state: RootState) => {
    const {
        currentItem,
        currentItemInfos,
        currentSuggestions,
        products,
        collectMode,
        restaurant
    } = state.ecommerceOrderState

    const { available } = state.featuresState
    const {
        fromCart,
        isImpersonalOperation,
        product,
        promotion,
        promotionOrigin,
        promotionToApply,
        coupon
    } = state.router.location.state || {}

    const featureExtrasAvailable = available.includes(
        featureCodes.CLICK_AND_COLLECT_EXTRA
    )
    const featureCustoAvailable = available.includes(
        featureCodes.CLICK_AND_COLLECT_CUSTOMIZATION
    )
    const featurePaidSideDishes = available.includes(
        featureCodes.PAID_SIDE_DISHES
    )

    let currentMenu
    let price = 0
    if (currentItemInfos) {
        const { id, categoryId } = currentItemInfos
        currentMenu = getMenuById(state.ecommerceCatalogState, id, categoryId)
        if (currentMenu) {
            price = currentMenu.price
        }
    }
    if (currentItem) {
        price = currentItem.finalPrice
        if (currentSuggestions.length > 0) {
            price += sumProductFinalPrices(currentSuggestions)
        }
    }

    const customizable =
        (fromCart &&
            (currentItem &&
            currentItem.promotions &&
            currentItem.promotions.length > 0
                ? currentMenu && currentMenu.customizable
                : true)) ||
        (currentMenu && currentMenu.customizable)

    return {
        currentOrder: currentItem,
        currentMenu,
        price,
        currentSuggestions,
        isImpersonalOperation,
        orderInit: product,
        isFromCart: fromCart || false,
        promotion:
            promotion ||
            (fromCart &&
                product &&
                product.promotions &&
                product.promotions[0]),
        promotionOrigin,
        extrasAvailable: customizable ? customizable : featureExtrasAvailable,
        custoAvailable: customizable ? customizable : featureCustoAvailable,
        featurePaidSideDishes,
        cartProducts: products,
        promotionToApply,
        coupon,
        collectMode
    }
}

const mapDispatchToProps = (d: Dispatch<any>) => ({
    addProductToCart: (product: PurchasableItem) =>
        d(addProductToCart(product)),
    updateProductInCart: (
        previousProduct: PurchasableItem,
        product: PurchasableItem
    ) => d(updateProductInCart(previousProduct, product)),
    setCurrentItem: (item: PurchasableItem) => d(setCurrentItem(item)),
    setCurrentSuggestions: (suggestions: Array<PurchasableItem>) =>
        d(setCurrentSuggestions(suggestions)),
    addMultipleProductsToCart: (products: Array<PurchasableItem>) =>
        d(addMultipleProductsToCart(products)),
    fetchAvailableFeatures: () => d(fetchAvailableFeatures()),
    pageMetadata: (payload: MetaShortDTO) => d(pageMetadata(payload)),
    sendMeta: (meta: EventMetadata) => d(eventMetadata(meta)),
    sendECommerceMeta: (meta: ECommerceMetadata) => d(eCommerceMetadata(meta)),
    navigate: (to: string, state?: any) => d(push({ pathname: to, state }))
})

const mergeProps = (stateProps, dispatchProps) => ({
    updateOrder: (position: number, newOrder: ?PurchasableItem) => {
        const { currentOrder } = stateProps
        if (currentOrder && currentOrder.steps) {
            const order = _.cloneDeep(currentOrder)
            order.steps[position] = newOrder
            order.finalPrice = getProductPrice(order).finalPrice
            dispatchProps.setCurrentItem(order)
        }
    },
    validateOrder: (
        isFromCart: boolean,
        orderInit: ?PurchasableItem,
        requiredForPromotion: ?string
    ) => {
        let { currentOrder } = stateProps
        const { currentSuggestions } = stateProps

        if (requiredForPromotion) {
            currentOrder = { ...currentOrder, requiredForPromotion }
        }

        if (currentOrder) {
            if (currentOrder.finalPrice === 0 && !!currentOrder.steps) {
                let sum = 0

                if (currentOrder.menuSize === MENU_SIZE_XL) {
                    return
                }

                currentOrder.steps.forEach(step => {
                    step.menuExtraPriceTTC && (sum += step.menuExtraPriceTTC)
                })
                if (sum > 0) {
                    currentOrder = {
                        ...currentOrder,
                        finalPrice: sum
                    }
                }
            }
            if (isFromCart && orderInit) {
                dispatchProps.updateProductInCart(orderInit, currentOrder)
            } else {
                if (currentSuggestions.length > 0) {
                    currentSuggestions.forEach(suggestion => {
                        dispatchProps.sendMeta({
                            ...eventsDefinitions.GA4_ECOMM_UPSELL,
                            page_category_level_3: 'Page produit',
                            item_id: suggestion.id,
                            item_name: suggestion.label,
                            item_price:
                                suggestion.finalPrice * suggestion.quantity,
                            quantity: suggestion.quantity
                        })
                        dispatchProps.sendMeta({
                            ...eventsDefinitions.ECOMM_UPSELL,
                            event_category: 'Page produit',
                            event_label: suggestion.label
                        })
                    })
                    dispatchProps.addMultipleProductsToCart(
                        currentSuggestions.concat(currentOrder)
                    )
                } else {
                    dispatchProps.addProductToCart(currentOrder)
                }
            }
        }
    },
    ...stateProps,
    ...dispatchProps
})

const Menu = connect(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(MenuComponent)

export { Menu }
